import React, { MouseEvent, useState } from "react";

import { Menu } from "@mui/material";
import MUIMenuItem from "@mui/material/MenuItem";

import Alert from "veneer/Alert";
import Box from "veneer/Box";
import Checkbox from "veneer/Checkbox";
import { IconButton } from "veneer/IconButton";
import SvgIcon from "veneer/SvgIcon";
import styled from "veneer/styled";
import Typography from "veneer/Typography";

export interface AvailableFilterOption {
  disabled?: boolean;
  id: string;
  label: string;
}

interface AvailableFiltersProps {
  dataTestId?: string;
  filters: ReadonlyArray<AvailableFilterOption>;
  onChange: (selectedFilterIds: ReadonlyArray<string>) => void;
  onInteractionChange?: (isInteracting: boolean) => void;
  selectedFilterIds: ReadonlyArray<string>;
}

interface ColumnRowProps {
  checked: boolean;
  dataTestId?: string;
  onToggle: () => void;
  option: AvailableFilterOption;
}

const FILTER_LIST_ICON =
  "M10.9718 18.9468C10.6003 18.9468 10.2889 18.8216 10.0378 18.5711C9.78645 18.3204 9.66078 18.0098 9.66078 17.6393C9.66078 17.269 9.78962 16.955 10.0473 16.6973C10.3049 16.4398 10.6178 16.3111 10.9858 16.3111H13.0283C13.3998 16.3111 13.7111 16.4398 13.9623 16.6973C14.2136 16.955 14.3393 17.269 14.3393 17.6393C14.3393 18.0098 14.2104 18.3204 13.9528 18.5711C13.6951 18.8216 13.3823 18.9468 13.0143 18.9468H10.9718ZM6.88703 13.3111C6.51553 13.3111 6.2042 13.1857 5.95303 12.9351C5.7017 12.6844 5.57603 12.3739 5.57603 12.0036C5.57603 11.6331 5.7017 11.3191 5.95303 11.0616C6.2042 10.8039 6.51553 10.6751 6.88703 10.6751H17.099C17.4704 10.6751 17.7817 10.8039 18.033 11.0616C18.2842 11.3191 18.4098 11.6331 18.4098 12.0036C18.4098 12.3739 18.2842 12.6844 18.033 12.9351C17.7817 13.1857 17.4704 13.3111 17.099 13.3111H6.88703ZM3.83053 7.67506C3.45903 7.67506 3.1477 7.54973 2.89653 7.29906C2.6452 7.04856 2.51953 6.73806 2.51953 6.36756C2.51953 5.99723 2.64836 5.68323 2.90603 5.42556C3.1637 5.1679 3.47653 5.03906 3.84453 5.03906H20.1695C20.541 5.03906 20.8524 5.1679 21.1035 5.42556C21.3549 5.68323 21.4805 5.99723 21.4805 6.36756C21.4805 6.73806 21.3517 7.04856 21.094 7.29906C20.8364 7.54973 20.5235 7.67506 20.1555 7.67506H3.83053Z";

function stopMenuEvent(event: {
  preventDefault: () => void;
  stopPropagation: () => void;
}) {
  event.preventDefault();
  event.stopPropagation();
}

function ColumnRow({ checked, dataTestId, onToggle, option }: ColumnRowProps) {
  const handleClick = (event: MouseEvent<HTMLElement>) => {
    stopMenuEvent(event);
    if (!option.disabled) {
      onToggle();
    }
  };

  return (
    <FullWidthMenuItem
      className={option.disabled ? "disabled" : undefined}
      disableRipple={option.disabled}
      onClick={handleClick}
    >
      <Box display="flex" justifyContent="center" minWidth={32} mr={1}>
        <Checkbox
          checked={checked}
          dataTestId={dataTestId}
          disabled={option.disabled}
          onChange={(_, event) => {
            event.stopPropagation();
            if (!option.disabled) {
              onToggle();
            }
          }}
        />
      </Box>
      <Typography component="span" flex={1}>
        {option.label}
      </Typography>
    </FullWidthMenuItem>
  );
}

export default function AvailableFilters({
  dataTestId,
  filters,
  onChange,
  onInteractionChange,
  selectedFilterIds,
}: AvailableFiltersProps) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleOpen = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    onInteractionChange?.(true);
  };

  const handleClose = () => {
    setAnchorEl(null);
    onInteractionChange?.(false);
  };

  const renderColumnRow = (option: AvailableFilterOption) => {
    const checked = selectedFilterIds.includes(option.id);
    const handleToggle = () => {
      const validIds = new Set(filters.map(({ id }) => id));
      const normalizedSelected = selectedFilterIds.filter((id) =>
        validIds.has(id),
      );
      const nextSelected = checked
        ? normalizedSelected.filter((id) => id !== option.id)
        : Array.from(new Set([...normalizedSelected, option.id]));
      onChange(nextSelected);
    };

    return (
      <ColumnRow
        checked={checked}
        dataTestId={
          dataTestId ? `${dataTestId}-option-${option.id}` : undefined
        }
        key={option.id}
        onToggle={handleToggle}
        option={option}
      />
    );
  };

  return (
    <>
      {!selectedFilterIds.length && (
        <Box mr={1}>
          <Alert dense severity="info">
            Add filters using the available filter options.
          </Alert>
        </Box>
      )}
      <IconButton
        aria-label="Available Filters"
        color="inherit"
        dataTestId={dataTestId}
        icon={<SvgIcon path={FILTER_LIST_ICON} />}
        onClick={handleOpen}
        title="Available Filters"
      />
      <Menu
        anchorEl={anchorEl}
        anchorOrigin={{ horizontal: "left", vertical: "top" }}
        keepMounted
        MenuListProps={{
          disablePadding: true,
          onClick: (event) => {
            event.stopPropagation();
          },
        }}
        onClose={handleClose}
        open={open}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
      >
        <ListItemsWrapper>{filters.map(renderColumnRow)}</ListItemsWrapper>
      </Menu>
    </>
  );
}

const ListItemsWrapper = styled(Box)({
  flex: "1 1 auto",
  minWidth: 244,
});

const FullWidthMenuItem = styled(MUIMenuItem)({
  width: "100%",
  "&.disabled": {
    cursor: "default",
  },
});
